import type { HTMLAttributes, ReactNode } from "react";

type Variant = "default" | "success" | "danger" | "transcript";

/**
 * Conteneur de contenu posé sur le fond teinté `ground` (§5.3/§6.4). Rend un
 * `<div>` et accepte ses attributs HTML. Au repos, PAS d'ombre : la carte
 * ressort par la teinte `surface` (blanc) ; l'ombre reste un signal réservé aux
 * flottants (cf. `shadow-float`). Rayon `lg`, padding 16px par défaut.
 *
 * Quand l'utiliser : regrouper de l'information (item, bloc de transcription,
 * encart succès/erreur). L'exception (succès / erreur) se signale par la teinte
 * + le filet, jamais par la couleur seule. Pour un conteneur cliquable plein,
 * préférer `ActionCard` (c'est un bouton).
 */
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Teinte sémantique (défaut `default`).
   * - `default` : blanc + filet neutre — le cas courant.
   * - `success` : mauve doux (état positif / prêt).
   * - `danger` : rouge doux + filet rouge (erreur).
   * - `transcript` : blanc + filet mauve (bloc de transcription).
   */
  variant?: Variant;
  children: ReactNode;
}

// Cartes sur fond `ground` : `surface` (blanc) pour ressortir par la teinte,
// sans ombre au repos (§5.3/§6.4). L'exception (succès/erreur) se signale par
// la teinte + le filet. Paires AA via les tokens -soft / -deep (§5.2).
const VARIANT: Record<Variant, string> = {
  default: "bg-surface border border-line text-ink",
  success: "bg-flambeau-50 border border-flambeau-200 text-flambeau-900",
  danger: "bg-red-soft border border-red text-red-deep",
  transcript: "bg-surface border border-flambeau-200 text-ink",
};

export function Card({ variant = "default", className = "", children, ...rest }: CardProps) {
  const classes = ["rounded-lg p-4", VARIANT[variant], className].filter(Boolean).join(" ");
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}
